import { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { SegBadge } from "./OpeningBalancesPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useAppCtx } from "../contexts/AppContext";
import { PeriodFilter, periodParams } from "../components/PeriodFilter";
import { PartnerAutocomplete, refreshPartnerCache } from "../components/PartnerAutocomplete";
import { toast } from "sonner";
import { Download, Upload, Trash2, CheckCircle2, FileText, FilePlus2, Mail, Pencil } from "lucide-react";
import SalesReconciliationTab from "./SalesReconciliationTab";

function fmtPeriodFrom(yyyyMmDd) {
    if (!yyyyMmDd) return "";
    const m = yyyyMmDd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return yyyyMmDd;
    return `${m[2]}/${m[3]}/${m[1]} 12:00:00 AM GMT`;
}
function fmtPeriodTo(yyyyMmDd) {
    if (!yyyyMmDd) return "";
    const m = yyyyMmDd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return yyyyMmDd;
    return `${m[2]}/${m[3]}/${m[1]} 11:59:59 PM GMT`;
}

export default function SalesInvoicesPage() {
    const { hasPerm, user } = useAuth();
    const canWrite = hasPerm("sales_invoices", "write");
    const isAdmin = user?.role === "admin";
    const { entities, selectedEntity, selectedSegment, currencies } = useAppCtx();
    const [drafts, setDrafts] = useState([]);
    const [posted, setPosted] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [uploadEntity, setUploadEntity] = useState("");
    const fileRef = useRef();
    const [partners, setPartners] = useState([]);
    const [genOpen, setGenOpen] = useState(false);
    const [genBusy, setGenBusy] = useState(false);
    const [genForm, setGenForm] = useState(blankGenForm());
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailBusy, setEmailBusy] = useState(false);
    const [emailInv, setEmailInv] = useState(null);
    const [emailTo, setEmailTo] = useState("");
    const [emailNote, setEmailNote] = useState("");
    const [editOpen, setEditOpen] = useState(false);
    const [editBusy, setEditBusy] = useState(false);
    const [editForm, setEditForm] = useState(null); // { id, invoice_number, invoice_date, partner_name, segment, currency, amount, fx_rate, description }
    const [numberHint, setNumberHint] = useState(""); // suggestion source description
    const [period, setPeriod] = useState({ from: null, to: null, preset: "this_month" });

    function blankGenForm() {
        const today = new Date().toISOString().slice(0, 10);
        return {
            entity_id: "",
            invoice_number: "",
            invoice_date: today,
            partner_id: "",
            period_from: "",
            period_to: "",
            currency: "USD",
            segment: "Voice",
            description: "",
            file: null,
        };
    }

    const buildParams = (status) => {
        const p = { status, ...periodParams(period) };
        if (selectedEntity !== "all") p.entity_id = selectedEntity;
        if (selectedSegment !== "all") p.segment = selectedSegment;
        return p;
    };

    const load = async () => {
        const [d, p, pr] = await Promise.all([
            api.get("/sales-invoices", { params: buildParams("draft") }),
            api.get("/sales-invoices", { params: buildParams("posted") }),
            api.get("/partners"),
        ]);
        setDrafts(d.data);
        setPosted(p.data);
        setPartners(pr.data || []);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [selectedEntity, selectedSegment, period]);

    // Split posted invoices into "not yet emailed" vs "sent to partner".
    // last_emailed_at is set by the backend on successful Mailgun send.
    const postedNotSent = useMemo(() => posted.filter((p) => !p.last_emailed_at), [posted]);
    const postedSent    = useMemo(() => posted.filter((p) =>  p.last_emailed_at), [posted]);

    // Refresh partner list whenever the Generate dialog opens, so newly
    // created partners (e.g. via OB upload) show up without reloading.
    useEffect(() => {
        if (!genOpen) return;
        api.get("/partners").then(({ data }) => setPartners(data || [])).catch(() => {});
        refreshPartnerCache();
    }, [genOpen]);

    // Suggest the next invoice number whenever (entity, partner) is set and
    // the user hasn't typed a number yet. Re-runs only when those two change.
    useEffect(() => {
        const { entity_id, partner_id, invoice_number } = genForm;
        if (!genOpen) return;
        if (!entity_id || !partner_id) {
            setNumberHint("");
            return;
        }
        if (invoice_number && invoice_number.trim()) {
            // user has already filled — don't overwrite, but refresh hint
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/sales-invoices/next-number", {
                    params: { entity_id, partner_id },
                });
                if (cancelled) return;
                if (data.next_number) {
                    setGenForm((f) => f.invoice_number ? f : { ...f, invoice_number: data.next_number });
                    setNumberHint(`Suggested from previous invoice ${data.based_on}. Editable.`);
                } else {
                    setNumberHint("First invoice for this partner — please enter a number manually.");
                }
            } catch {
                setNumberHint("");
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [genForm.entity_id, genForm.partner_id, genOpen]);

    const downloadTemplate = async () => {
        const token = localStorage.getItem("erp_access_token");
        const res = await fetch(`${API}/sales-invoices/template`, {
            credentials: "include",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "sales_invoices_template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const onFile = async (e) => {
        if (!uploadEntity) return toast.error("Select target entity for the upload");
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(`/sales-invoices/upload?entity_id=${uploadEntity}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Imported ${data.inserted} draft invoice(s) — verify before posting`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            e.target.value = "";
        }
    };

    const toggleAll = () => {
        if (selectedIds.size === drafts.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(drafts.map((d) => d.id)));
    };
    const toggle = (id) => {
        const s = new Set(selectedIds);
        s.has(id) ? s.delete(id) : s.add(id);
        setSelectedIds(s);
    };

    const post = async () => {
        if (!selectedIds.size) return toast.error("Select at least one row");
        if (!window.confirm(`Post ${selectedIds.size} invoice(s) to the ledger?`)) return;
        try {
            const { data } = await api.post("/sales-invoices/post", { invoice_ids: [...selectedIds] });
            toast.success(`Posted ${data.posted} invoice(s)`);
            setSelectedIds(new Set());
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const removeDraft = async (id) => {
        if (!window.confirm("Delete this draft invoice?")) return;
        await api.delete(`/sales-invoices/${id}`);
        await load();
    };

    const openEditDraft = (inv) => {
        setEditForm({
            id: inv.id,
            invoice_number: inv.invoice_number || "",
            invoice_date: inv.invoice_date || "",
            partner_name: inv.partner_name || "",
            segment: inv.segment || "Voice",
            currency: inv.currency || "USD",
            amount: String(inv.amount ?? ""),
            fx_rate: inv.fx_rate ? String(inv.fx_rate) : "",
            description: inv.description || "",
        });
        setEditOpen(true);
    };

    const saveEditDraft = async () => {
        if (!editForm) return;
        if (!editForm.invoice_number.trim() || !editForm.invoice_date || !editForm.partner_name.trim() || !editForm.amount) {
            toast.error("Invoice #, date, partner and amount are required");
            return;
        }
        setEditBusy(true);
        try {
            const body = {
                invoice_number: editForm.invoice_number.trim(),
                invoice_date: editForm.invoice_date,
                partner_name: editForm.partner_name.trim(),
                segment: editForm.segment,
                currency: editForm.currency,
                amount: parseFloat(editForm.amount),
                description: editForm.description,
            };
            if (editForm.fx_rate && parseFloat(editForm.fx_rate) > 0) {
                body.fx_rate = parseFloat(editForm.fx_rate);
            }
            await api.put(`/sales-invoices/${editForm.id}`, body);
            toast.success(`Draft ${body.invoice_number} updated`);
            setEditOpen(false);
            setEditForm(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        } finally {
            setEditBusy(false);
        }
    };

    const removePosted = async (inv) => {
        const msg = `DANGER: Delete posted invoice ${inv.invoice_number} (${inv.partner_name}, ${inv.currency} ${Number(inv.amount).toFixed(2)})?\n\nThis is permanent and removes the receivable/payable from all reports. Type the invoice number to confirm.`;
        const typed = window.prompt(msg);
        if (typed !== inv.invoice_number) return toast.error("Invoice number mismatch — cancelled");
        try {
            await api.delete(`/sales-invoices/${inv.id}?force=true`);
            toast.success(`Deleted ${inv.invoice_number}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    const openEmail = (inv) => {
        const partner = partners.find((p) => p.id === inv.partner_id);
        const defaultTo = (partner?.emails || []).join(", ");
        setEmailInv(inv);
        setEmailTo(defaultTo);
        setEmailNote("");
        setEmailOpen(true);
    };

    const sendEmail = async () => {
        if (!emailInv) return;
        const recipients = (emailTo || "")
            .replace(/;/g, ",")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        if (!recipients.length) return toast.error("At least one recipient email is required");
        setEmailBusy(true);
        try {
            const { data } = await api.post(`/sales-invoices/${emailInv.id}/email`, {
                to: recipients,
                note: emailNote || null,
            });
            toast.success(`Email queued to ${data.recipients.length} recipient(s)`);
            setEmailOpen(false);
            setEmailInv(null);
            await load();
        } catch (e) {
            const detail = e.response?.data?.detail || "Failed to send";
            toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
        } finally {
            setEmailBusy(false);
        }
    };

    const downloadInvoicePdf = async (inv) => {
        try {
            const token = localStorage.getItem("erp_access_token");
            const res = await fetch(`${API}/sales-invoices/${inv.id}/pdf`, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `invoice_${inv.invoice_number}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e.message || "Could not download PDF");
        }
    };

    const generateInvoice = async () => {
        const { entity_id, invoice_number, invoice_date, partner_id, currency, segment, file } = genForm;
        if (!entity_id || !invoice_number || !invoice_date || !partner_id || !currency || !file) {
            return toast.error("Entity, partner, invoice #, date, currency and server PDF are required");
        }
        setGenBusy(true);
        try {
            const fd = new FormData();
            fd.append("server_pdf", file);
            const params = new URLSearchParams({
                entity_id,
                invoice_number,
                invoice_date,
                partner_id,
                period_from: genForm.period_from || "",
                period_to: genForm.period_to || "",
                currency,
                segment,
                description: genForm.description || "",
            });
            const token = localStorage.getItem("erp_access_token");
            const res = await fetch(`${API}/sales-invoices/generate?${params.toString()}`, {
                method: "POST",
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: fd,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `invoice_${invoice_number}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`Invoice ${invoice_number} generated and posted`);
            setGenOpen(false);
            setGenForm(blankGenForm());
            await load();
        } catch (e) {
            toast.error(e.message || "Generate failed");
        } finally {
            setGenBusy(false);
        }
    };


    const entityName = (id) => entities.find((e) => e.id === id)?.name || "—";

    return (
        <div data-testid="sales-invoices-page">
            <PageHeader
                title="Sales Invoices"
                eyebrow="Transactions"
                description="CSV-driven invoice ingestion. Drafts must be verified before posting to the ledger."
                action={
                    <div className="flex gap-2">
                        {canWrite && (
                            <Dialog open={genOpen} onOpenChange={setGenOpen}>
                                <DialogTrigger asChild>
                                    <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="invoice-generate-btn">
                                        <FilePlus2 size={14} className="mr-1.5" /> Generate Invoice
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="rounded-sm max-w-2xl" data-testid="invoice-generate-dialog">
                                    <DialogHeader>
                                        <DialogTitle>Generate Invoice from Server PDF</DialogTitle>
                                    </DialogHeader>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <Field label="Entity">
                                            <Select value={genForm.entity_id} onValueChange={(v) => setGenForm({ ...genForm, entity_id: v, partner_id: "", invoice_number: "" })}>
                                                <SelectTrigger className="rounded-sm" data-testid="gen-entity-select"><SelectValue placeholder="Select entity"/></SelectTrigger>
                                                <SelectContent>{entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label="Segment">
                                            <Select value={genForm.segment} onValueChange={(v) => setGenForm({ ...genForm, segment: v })}>
                                                <SelectTrigger className="rounded-sm" data-testid="gen-segment-select"><SelectValue/></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Voice">Voice (USD base)</SelectItem>
                                                    <SelectItem value="SMS">SMS (EUR base)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <div className="col-span-2">
                                            <Field label="Partner (Customer)">
                                                <PartnerAutocomplete
                                                    valueMode="id"
                                                    value={genForm.partner_id}
                                                    onChange={(id) => setGenForm({ ...genForm, partner_id: id, invoice_number: "" })}
                                                    placeholder={genForm.entity_id ? "Type to search partner — must include address" : "Select an entity first"}
                                                    disabled={!genForm.entity_id}
                                                    testId="gen-partner-select"
                                                />
                                            </Field>
                                        </div>
                                        <Field label="Invoice Number">
                                            <Input
                                                value={genForm.invoice_number}
                                                onChange={(e) => setGenForm({ ...genForm, invoice_number: e.target.value })}
                                                className="rounded-sm h-9 font-mono"
                                                placeholder={genForm.partner_id ? "Auto-suggested when partner has prior invoices" : "Select partner first"}
                                                disabled={!genForm.partner_id}
                                                data-testid="gen-invoice-number-input"
                                            />
                                            {numberHint && genForm.partner_id && (
                                                <div className="text-[10.5px] text-slate-500 mt-1">{numberHint}</div>
                                            )}
                                        </Field>
                                        <Field label="Invoice Date">
                                            <Input type="date" value={genForm.invoice_date} onChange={(e) => setGenForm({ ...genForm, invoice_date: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="gen-invoice-date-input"/>
                                        </Field>
                                        <Field label="Period From">
                                            <Input type="date" value={genForm.period_from} onChange={(e) => setGenForm({ ...genForm, period_from: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="gen-period-from-input"/>
                                            {genForm.period_from && (
                                                <div className="text-[10.5px] text-slate-500 mt-1 font-mono">→ {fmtPeriodFrom(genForm.period_from)}</div>
                                            )}
                                        </Field>
                                        <Field label="Period To">
                                            <Input type="date" value={genForm.period_to} onChange={(e) => setGenForm({ ...genForm, period_to: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="gen-period-to-input"/>
                                            {genForm.period_to && (
                                                <div className="text-[10.5px] text-slate-500 mt-1 font-mono">→ {fmtPeriodTo(genForm.period_to)}</div>
                                            )}
                                        </Field>
                                        <Field label="Currency">
                                            <Select value={genForm.currency} onValueChange={(v) => setGenForm({ ...genForm, currency: v })}>
                                                <SelectTrigger className="rounded-sm" data-testid="gen-currency-select"><SelectValue/></SelectTrigger>
                                                <SelectContent>
                                                    {["USD","EUR","AED","KWD","INR","USDT"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label="Line description (optional)">
                                            <Input value={genForm.description} onChange={(e) => setGenForm({ ...genForm, description: e.target.value })} placeholder="A - Z Wholesale Voice Termination" className="rounded-sm h-9" data-testid="gen-desc-input"/>
                                        </Field>
                                        <div className="col-span-2">
                                            <Field label="Server PDF (call/SMS detail report)">
                                                <Input
                                                    type="file"
                                                    accept="application/pdf"
                                                    onChange={(e) => setGenForm({ ...genForm, file: e.target.files?.[0] || null })}
                                                    className="rounded-sm h-10 font-mono text-[12px] cursor-pointer"
                                                    data-testid="gen-server-pdf-input"
                                                />
                                            </Field>
                                            <div className="text-[11.5px] text-slate-500 mt-1.5">
                                                Total amount, total minutes and the destination breakdown will be auto-extracted. The invoice will be auto-posted on generation.
                                            </div>
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button variant="ghost" onClick={() => setGenOpen(false)}>Cancel</Button>
                                        <Button onClick={generateInvoice} disabled={genBusy} className="rounded-sm bg-[#002FA7]" data-testid="gen-submit-btn">
                                            {genBusy ? "Generating…" : "Generate & Download"}
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        )}
                        <Button variant="outline" className="rounded-sm" onClick={downloadTemplate} data-testid="invoice-template-btn">
                            <Download size={14} className="mr-1.5" /> Bulk CSV Template
                        </Button>
                    </div>
                }
            />

            {canWrite && (
                <div className="csv-dropzone p-6 mb-6 flex items-center justify-between" data-testid="invoice-uploader">
                    <div className="flex items-center gap-4">
                        <Upload size={28} className="text-slate-400" />
                        <div>
                            <div className="text-[13.5px] font-medium">Upload sales invoice CSV</div>
                            <div className="text-[11.5px] text-slate-500">
                                Columns: Invoice_Date, Invoice_Number, Segment, Partner_Name, Currency, Amount, Description
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Select value={uploadEntity} onValueChange={setUploadEntity}>
                            <SelectTrigger className="rounded-sm w-56" data-testid="invoice-upload-entity-select">
                                <SelectValue placeholder="Select target entity" />
                            </SelectTrigger>
                            <SelectContent>
                                {entities.map((e) => (
                                    <SelectItem key={e.id} value={e.id}>
                                        {e.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv"
                            onChange={onFile}
                            hidden
                            data-testid="invoice-file-input"
                        />
                        <Button
                            onClick={() => fileRef.current?.click()}
                            className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]"
                            data-testid="invoice-upload-btn"
                        >
                            Choose CSV…
                        </Button>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mb-3">
                <PeriodFilter initialPreset="this_month" onChange={setPeriod}/>
            </div>

            <Tabs defaultValue="draft" className="w-full">
                <TabsList className="rounded-sm bg-slate-100">
                    <TabsTrigger value="draft" className="rounded-sm" data-testid="invoice-tab-draft">
                        Drafts ({drafts.length})
                    </TabsTrigger>
                    <TabsTrigger value="posted" className="rounded-sm" data-testid="invoice-tab-posted">
                        Posted ({postedNotSent.length})
                    </TabsTrigger>
                    <TabsTrigger value="sent" className="rounded-sm" data-testid="invoice-tab-sent">
                        Sent to Partner ({postedSent.length})
                    </TabsTrigger>
                    <TabsTrigger value="recon" className="rounded-sm" data-testid="invoice-tab-recon">
                        Data Reconciliation
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="draft">
                    {drafts.length > 0 && (
                        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 px-4 py-2 mb-3 mt-3">
                            <div className="text-[12.5px] text-amber-900">
                                <strong>{selectedIds.size}</strong> of {drafts.length} selected · only checked rows will post.
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={toggleAll} className="rounded-sm h-8" data-testid="invoice-select-all">
                                    {selectedIds.size === drafts.length ? "Unselect all" : "Select all"}
                                </Button>
                                {canWrite && (
                                    <Button
                                        size="sm"
                                        onClick={post}
                                        className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f] h-8"
                                        data-testid="invoice-post-btn"
                                    >
                                        <CheckCircle2 size={14} className="mr-1" /> Post to Ledger
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                    <InvoiceTable
                        rows={drafts}
                        selectable
                        selectedIds={selectedIds}
                        toggle={toggle}
                        toggleAll={toggleAll}
                        entityName={entityName}
                        onDelete={canWrite ? removeDraft : null}
                        onEdit={canWrite ? openEditDraft : null}
                    />
                </TabsContent>
                <TabsContent value="posted">
                    <div className="mt-3" />
                    <InvoiceTable
                        rows={postedNotSent}
                        entityName={entityName}
                        onDownload={downloadInvoicePdf}
                        onEmail={canWrite ? openEmail : null}
                        onDelete={isAdmin ? (id) => {
                            const inv = postedNotSent.find((p) => p.id === id);
                            if (inv) removePosted(inv);
                        } : null}
                    />
                </TabsContent>
                <TabsContent value="sent">
                    <div className="mt-3" />
                    <InvoiceTable
                        rows={postedSent}
                        entityName={entityName}
                        onDownload={downloadInvoicePdf}
                        onEmail={canWrite ? openEmail : null}
                        showSentInfo
                        onDelete={isAdmin ? (id) => {
                            const inv = postedSent.find((p) => p.id === id);
                            if (inv) removePosted(inv);
                        } : null}
                    />
                </TabsContent>
                <TabsContent value="recon">
                    <div className="mt-3" />
                    <SalesReconciliationTab
                        canWrite={canWrite}
                        segment={selectedSegment}
                        period={periodParams(period)}
                        entities={entities}
                        currencies={currencies}
                        onInvoiced={load}
                    />
                </TabsContent>
            </Tabs>

            <Dialog open={emailOpen} onOpenChange={(o) => { if (!o) { setEmailOpen(false); setEmailInv(null); } }}>
                <DialogContent className="rounded-sm max-w-lg" data-testid="email-invoice-dialog">
                    <DialogHeader>
                        <DialogTitle>Email invoice {emailInv?.invoice_number}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-[12px] text-slate-500">
                            The invoice PDF will be attached to a branded email and sent via Mailgun.
                        </div>
                        <Field label="Recipients (comma separated)">
                            <Input
                                value={emailTo}
                                onChange={(e) => setEmailTo(e.target.value)}
                                className="rounded-sm h-9 font-mono text-[12px]"
                                placeholder="customer@example.com, accounts@example.com"
                                data-testid="email-to-input"
                            />
                        </Field>
                        {(() => {
                            const ent = emailInv ? entities.find((e) => e.id === emailInv.entity_id) : null;
                            const cc = ent?.invoice_settings?.email_cc || [];
                            if (!cc.length) return null;
                            return (
                                <div className="text-[11.5px] text-slate-600 bg-blue-50 border border-blue-200 px-2.5 py-2 rounded-sm" data-testid="email-auto-cc-hint">
                                    <div className="text-[10px] uppercase tracking-widest text-[#002FA7] font-semibold mb-0.5">Auto-CC (from Invoice Settings)</div>
                                    <div className="font-mono text-[11.5px]">{cc.join(", ")}</div>
                                </div>
                            );
                        })()}
                        <Field label="Message (optional — leave blank to use the standard template)">
                            <textarea
                                value={emailNote}
                                onChange={(e) => setEmailNote(e.target.value)}
                                rows={3}
                                className="w-full rounded-sm border border-slate-200 px-3 py-2 text-[13px]"
                                placeholder="Leave blank — the email body uses your standard template (entity name + billing period)."
                                data-testid="email-note-input"
                            />
                        </Field>
                        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded-sm">
                            Sandbox notice: until your domain is verified in Mailgun, emails will only be delivered to recipients added under <span className="font-mono">Authorized Recipients</span>.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setEmailOpen(false); setEmailInv(null); }} data-testid="email-cancel-btn">Cancel</Button>
                        <Button onClick={sendEmail} disabled={emailBusy} className="rounded-sm bg-[#002FA7]" data-testid="email-send-btn">
                            {emailBusy ? "Sending…" : "Send email"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setEditForm(null); } }}>
                <DialogContent className="rounded-sm max-w-2xl" data-testid="edit-draft-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit draft invoice {editForm?.invoice_number || ""}</DialogTitle>
                    </DialogHeader>
                    {editForm && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Invoice #">
                                <Input
                                    value={editForm.invoice_number}
                                    onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })}
                                    className="rounded-sm h-9 font-mono"
                                    data-testid="edit-invoice-number"
                                />
                            </Field>
                            <Field label="Invoice date">
                                <Input
                                    type="date"
                                    value={editForm.invoice_date}
                                    onChange={(e) => setEditForm({ ...editForm, invoice_date: e.target.value })}
                                    className="rounded-sm h-9 font-mono"
                                    data-testid="edit-invoice-date"
                                />
                            </Field>
                            <Field label="Partner">
                                <PartnerAutocomplete
                                    value={editForm.partner_name}
                                    onChange={(v) => setEditForm({ ...editForm, partner_name: v })}
                                    placeholder="Type to search partner…"
                                    testId="edit-partner-input"
                                />
                            </Field>
                            <Field label="Segment">
                                <Select value={editForm.segment} onValueChange={(v) => setEditForm({ ...editForm, segment: v, fx_rate: "" })}>
                                    <SelectTrigger className="rounded-sm" data-testid="edit-segment-select"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Voice">Voice (USD)</SelectItem>
                                        <SelectItem value="SMS">SMS (EUR)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Currency">
                                <Select value={editForm.currency} onValueChange={(v) => setEditForm({ ...editForm, currency: v, fx_rate: "" })}>
                                    <SelectTrigger className="rounded-sm" data-testid="edit-currency-select"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Amount">
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={editForm.amount}
                                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                                    className="rounded-sm h-9 font-mono"
                                    data-testid="edit-amount-input"
                                />
                            </Field>
                            <Field label="FX rate override (optional, blank = auto)">
                                <Input
                                    type="number"
                                    step="0.000001"
                                    value={editForm.fx_rate}
                                    onChange={(e) => setEditForm({ ...editForm, fx_rate: e.target.value })}
                                    className="rounded-sm h-9 font-mono"
                                    placeholder="auto from FX master"
                                    data-testid="edit-fx-input"
                                />
                            </Field>
                            <div className="sm:col-span-2">
                                <Field label="Description / remarks">
                                    <Input
                                        value={editForm.description}
                                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                        className="rounded-sm h-9"
                                        data-testid="edit-description-input"
                                    />
                                </Field>
                            </div>
                            <div className="sm:col-span-2 text-[11px] text-slate-500">
                                Base amount and FX rate are recomputed on save. The Voice base is USD, SMS base is EUR.
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setEditOpen(false); setEditForm(null); }} data-testid="edit-cancel-btn">Cancel</Button>
                        <Button onClick={saveEditDraft} disabled={editBusy} className="rounded-sm bg-[#002FA7]" data-testid="edit-save-btn">
                            {editBusy ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function InvoiceTable({ rows, selectable, selectedIds, toggle, toggleAll, entityName, onDelete, onDownload, onEmail, onEdit, showSentInfo }) {
    return (
        <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-[13px] financial min-w-[900px]">
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                        {selectable && (
                            <th className="px-3 py-2.5 w-10">
                                <Checkbox
                                    checked={rows.length > 0 && selectedIds.size === rows.length}
                                    onCheckedChange={toggleAll}
                                    data-testid="invoice-header-checkbox"
                                />
                            </th>
                        )}
                        <th className="px-3 py-2.5">Date</th>
                        <th className="px-3 py-2.5">Invoice #</th>
                        <th className="px-3 py-2.5">Entity</th>
                        <th className="px-3 py-2.5">Segment</th>
                        <th className="px-3 py-2.5">Partner</th>
                        <th className="px-3 py-2.5 num">Amount</th>
                        <th className="px-3 py-2.5">Cur</th>
                        <th className="px-3 py-2.5 num">FX</th>
                        <th className="px-3 py-2.5 num">Base</th>
                        <th className="px-3 py-2.5">Base Cur</th>
                        {showSentInfo && <th className="px-3 py-2.5">Sent</th>}
                        {(onDelete || onDownload || onEmail || onEdit) && <th className="px-3 py-2.5 w-32"></th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={(selectable ? 12 : 11) + (showSentInfo ? 1 : 0)} className="text-center text-slate-500 py-10">
                                No invoices
                            </td>
                        </tr>
                    )}
                    {rows.map((r) => (
                        <tr key={r.id} className="border-b border-slate-100" data-testid={`invoice-row-${r.id}`}>
                            {selectable && (
                                <td className="px-3 py-2">
                                    <Checkbox
                                        checked={selectedIds.has(r.id)}
                                        onCheckedChange={() => toggle(r.id)}
                                        data-testid={`invoice-checkbox-${r.id}`}
                                    />
                                </td>
                            )}
                            <td className="px-3 py-2 font-mono">{r.invoice_date}</td>
                            <td className="px-3 py-2 font-mono">
                                {r.invoice_number}
                                {r.generated && (
                                    <span className="ml-1.5 inline-flex items-center text-[9.5px] uppercase tracking-widest text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-sm">
                                        <FileText size={9} className="mr-0.5" /> PDF
                                    </span>
                                )}
                            </td>
                            <td className="px-3 py-2">{entityName(r.entity_id)}</td>
                            <td className="px-3 py-2"><SegBadge segment={r.segment} /></td>
                            <td className="px-3 py-2">{r.partner_name}</td>
                            <td className="px-3 py-2 num">{Number(r.amount).toFixed(2)}</td>
                            <td className="px-3 py-2 font-mono">{r.currency}</td>
                            <td className="px-3 py-2 num">{Number(r.fx_rate).toFixed(4)}</td>
                            <td className="px-3 py-2 num font-semibold">{Number(r.amount_base).toFixed(2)}</td>
                            <td className="px-3 py-2 font-mono text-slate-500">{r.base_currency}</td>
                            {showSentInfo && (
                                <td className="px-3 py-2 text-[11.5px] text-emerald-700 whitespace-nowrap" title={(r.last_emailed_to || []).join(", ")}>
                                    {r.last_emailed_at ? r.last_emailed_at.slice(0, 10) : ""}
                                    {r.last_emailed_to?.length ? <div className="text-[10px] text-slate-500 font-mono truncate max-w-[200px]">{r.last_emailed_to[0]}{r.last_emailed_to.length > 1 ? ` +${r.last_emailed_to.length - 1}` : ""}</div> : null}
                                </td>
                            )}
                            {(onDelete || onDownload || onEmail || onEdit) && (
                                <td className="px-3 py-2 whitespace-nowrap text-right">
                                    {onEdit && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onEdit(r)}
                                            className="h-7 px-2 text-slate-700"
                                            data-testid={`invoice-edit-${r.id}`}
                                            title="Edit draft"
                                        >
                                            <Pencil size={13} />
                                        </Button>
                                    )}
                                    {onEmail && r.generated && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onEmail(r)}
                                            className="h-7 px-2 text-blue-700"
                                            data-testid={`invoice-email-${r.id}`}
                                            title={r.last_emailed_at ? `Re-send (last sent ${r.last_emailed_at.slice(0,10)})` : "Email to customer"}
                                        >
                                            <Mail size={13} />
                                        </Button>
                                    )}
                                    {onDownload && r.generated && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onDownload(r)}
                                            className="h-7 px-2 text-blue-700"
                                            data-testid={`invoice-download-${r.id}`}
                                            title="Download PDF"
                                        >
                                            <Download size={13} />
                                        </Button>
                                    )}
                                    {onDelete && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onDelete(r.id)}
                                            className="h-7 px-2 text-red-600"
                                            data-testid={`invoice-delete-${r.id}`}
                                        >
                                            <Trash2 size={13} />
                                        </Button>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
